// Global Instructions Rule Applied!

import React, { useState, useEffect, useCallback, useMemo, forwardRef } from 'react'
import { debounce } from 'lodash'
import Select from '../../Select.jsx'
import { useProspectSearch } from '../../../hooks_deprecated/useProspectSearch.js'
import { InputSize, FieldValue } from '../types.ts'
import { SelectListEntry } from '../../../../state_manager/data_models/select_list_entry.ts'

export interface ProspectSelectProps {
  value: FieldValue
  onChange?: (value: string | string[] | FieldValue, option?: SelectListEntry | SelectListEntry[] | null) => void
  onSelect?: (value: string, option?: SelectListEntry) => void
  onClear?: () => void
  mode?: 'form' | 'inline' | 'cell' | 'single' | 'multiple'
  selectMode?: 'single' | 'multiple'
  required?: boolean
  placeholder?: string
  size?: InputSize
  error?: boolean
  style?: React.CSSProperties
  className?: string
  autoFocus?: boolean
  onKeyDown?: (e: React.KeyboardEvent) => void
  disabled?: boolean
}

export const ProspectSelect = forwardRef<any, ProspectSelectProps>(
  (
    {
      value,
      onChange,
      onSelect,
      onClear,
      mode = 'form',
      selectMode = 'single',
      required = false,
      placeholder = 'Search prospects...',
      size = 'small',
      error = false,
      style = {},
      className = '',
      autoFocus,
      onKeyDown,
      disabled,
      ...selectProps
    },
    ref
  ) => {
    const {
      searchProspects,
      loading: prospectSearchLoading,
      results: prospectSearchResults,
      clearResults: clearProspectResults,
      hasSearched: prospectHasSearched
    } = useProspectSearch()

    const isMultiple = selectMode === 'multiple'
    const [selectedProspect, setSelectedProspect] = useState<SelectListEntry | null>(null)
    const [selectedProspects, setSelectedProspects] = useState<SelectListEntry[]>([])

    const initialValueOptions = useMemo<SelectListEntry[]>(() => {
      if (!value) return []

      if (isMultiple) {
        if (Array.isArray(value)) {
          return value.map((v: any) => {
            if (typeof v === 'object' && v.value !== undefined && v.label) {
              return {
                label: v.label,
                value: v.value,
                name: v.name || v.label
              }
            }
            return {
              label: v,
              value: v,
              name: v
            }
          })
        }
        return []
      } else {
        if (typeof value === 'object' && value && (value as any).value !== undefined && (value as any).label) {
          const v = value as any
          return [
            {
              label: v.label,
              value: v.value,
              name: v.name || v.label
            }
          ]
        }
        return []
      }
    }, [value, isMultiple])

    const initializeProspectSelection = useCallback(
      (prospectVal: any) => {
        if (isMultiple) {
          if (Array.isArray(prospectVal) && prospectVal.length > 0) {
            const prospects = prospectVal.map((v: any) => {
              if (typeof v === 'object' && v.value !== undefined) {
                return {
                  label: v.label || v.name || v.value,
                  value: v.value,
                  name: v.name || v.label || v.value
                }
              }
              return {
                label: v,
                value: v,
                name: v
              }
            })
            setSelectedProspects(prospects)
          } else {
            setSelectedProspects([])
          }
        } else {
          let prospectId: string = ''
          let prospectLabel: string = ''

          if (typeof prospectVal === 'object' && prospectVal !== null) {
            prospectId = (prospectVal as any).value || (prospectVal as any).id || ''
            prospectLabel = (prospectVal as any).label || (prospectVal as any).name || ''
          } else if (prospectVal) {
            prospectId = String(prospectVal)
          }

          const currentValue = selectedProspect?.value || ''
          const valueChanged = prospectId !== currentValue

          if (prospectVal && (!selectedProspect || valueChanged)) {
            if (prospectLabel && prospectId) {
              const selectListEntry: SelectListEntry = {
                label: prospectLabel,
                value: prospectId,
                name: prospectLabel
              }
              setSelectedProspect(selectListEntry)
            } else if (prospectId) {
              const existingProspect = prospectSearchResults.find(
                (prospect: any) => prospect.value === prospectId || prospect.label === prospectId
              )

              if (existingProspect) {
                const selectListEntry: SelectListEntry = {
                  label: existingProspect.label,
                  value: existingProspect.value,
                  name: existingProspect.label
                }
                setSelectedProspect(selectListEntry)
              } else {
                const tempProspect: SelectListEntry = {
                  label: prospectLabel || prospectId,
                  value: prospectId,
                  name: prospectLabel || prospectId
                }
                setSelectedProspect(tempProspect)
              }
            }
          } else if (!prospectVal) {
            setSelectedProspect(null)
          }
        }
      },
      [prospectSearchResults, selectedProspect, isMultiple]
    )

    useEffect(() => {
      initializeProspectSelection(value)
    }, [value, initializeProspectSelection])

    useEffect(() => {
      if (isMultiple) {
        setSelectedProspects(initialValueOptions)
      }
    }, [initialValueOptions, isMultiple])

    const handleProspectSelect = useCallback(
      (selectedValue: any, option: any) => {
        if (isMultiple) {
          if (Array.isArray(selectedValue)) {
            const transformedValues = selectedValue.map((val: any) => {
              const existingProspect = Array.isArray(value)
                ? value.find((v: any) => {
                    const vValue = typeof v === 'object' && v.value !== undefined ? v.value : v
                    return vValue === val
                  })
                : null

              const existingOption =
                prospectSearchResults.find((opt: any) => opt.value === val) ||
                initialValueOptions.find((opt) => opt.value === val)

              return {
                label: existingProspect?.label || existingOption?.label || existingOption?.name || val,
                value: val,
                name: existingProspect?.name || existingOption?.name || existingOption?.label || val
              }
            })
            setSelectedProspects(transformedValues)
            onChange?.(transformedValues)
          } else {
            setSelectedProspects([])
            onChange?.([])
          }
        } else {
          if (option) {
            setSelectedProspect(option)
            onChange?.(selectedValue, option)
            onSelect?.(selectedValue, option)
          }
        }
      },
      [onChange, onSelect, isMultiple, value, prospectSearchResults, initialValueOptions]
    )

    const handleClear = useCallback(() => {
      if (isMultiple) {
        setSelectedProspects([])
        onChange?.([])
      } else {
        setSelectedProspect(null)
        onChange?.('', null)
      }
      clearProspectResults()
      onClear?.()
    }, [onChange, onClear, clearProspectResults, isMultiple])

    const debouncedSearch = useMemo(
      () =>
        debounce((searchValue: string) => {
          if (searchValue && searchValue.length >= 2) {
            searchProspects(searchValue)
          }
        }, 300),
      [searchProspects]
    )

    const handleSearch = useCallback(
      (searchValue: string) => {
        if (!searchValue) {
          clearProspectResults()
        } else {
          debouncedSearch(searchValue)
        }
      },
      [debouncedSearch, clearProspectResults]
    )

    const transformedResults: SelectListEntry[] = prospectSearchResults.map((result: any) => ({
      label: result.label,
      value: result.value,
      name: result.label
    }))

    const getDisplayValue = () => {
      if (!value) return undefined

      if (isMultiple) {
        if (Array.isArray(value)) {
          return value.map((v: any) => {
            if (typeof v === 'object' && v.value !== undefined) {
              return v.value
            }
            return typeof v === 'string' ? v : v
          })
        }
        return []
      } else {
        if (typeof value === 'object' && value && (value as any).value !== undefined) {
          return (value as any).value
        }
        return typeof value === 'object' && value ? (value as any).value : value
      }
    }

    const displayOptions = useMemo(() => {
      if (isMultiple) {
        if (transformedResults.length > 0) {
          const searchValues = new Set(transformedResults.map((opt) => opt.value))
          const mergedInitialOptions = initialValueOptions.filter((opt) => !searchValues.has(opt.value))
          return [...transformedResults, ...mergedInitialOptions]
        }
        return initialValueOptions
      } else {
        if (selectedProspect) {
          return [selectedProspect, ...transformedResults.filter((result) => result.value !== selectedProspect.value)]
        }
        return transformedResults
      }
    }, [transformedResults, selectedProspect, initialValueOptions, isMultiple])

    const multipleDisplayValue = useMemo(() => {
      if (isMultiple) {
        if (selectedProspects.length > 0) {
          return selectedProspects.map((p) => p.value)
        }
        return initialValueOptions.map((opt) => opt.value)
      }
      return undefined
    }, [isMultiple, selectedProspects, initialValueOptions])

    const options = displayOptions

    const placeholderText = required ? `${placeholder}` : placeholder

    return (
      <Select
        ref={ref}
        mode={isMultiple ? 'multiple' : undefined}
        value={isMultiple ? multipleDisplayValue : selectedProspect ? selectedProspect.value : undefined}
        onChange={handleProspectSelect}
        onClear={handleClear}
        onSearch={handleSearch}
        loading={prospectSearchLoading}
        options={options}
        placeholder={placeholderText}
        size={size}
        style={{
          width: '100%',
          ...style,
          borderColor: style.borderColor || (error ? '#ff4d4f' : undefined)
        }}
        className={className}
        status={error ? 'error' : undefined}
        filterOption={false}
        notFoundContent={
          prospectSearchLoading
            ? 'Searching...'
            : prospectHasSearched
              ? 'No prospects found'
              : 'Type to start searching'
        }
        autoFocus={autoFocus}
        onKeyDown={onKeyDown}
        disabled={disabled}
        allowClear
        {...selectProps}
      />
    )
  }
)

ProspectSelect.displayName = 'ProspectSelect'
